import { useState } from "react"

import { cn } from "@/lib/utils"
import {
  tapToAskCopy,
  tapToAskLabels,
  tapToAskQuestions,
  tapToAskLabelMapping,
  type QuestionLabel,
  type TagCategory,
} from "../config/tap-to-ask"

import Chevron from "@/assets/svg/tapToAsk/chevron_right.svg?react"
import LabelLadies from "@/assets/svg/tapToAsk/trend_ladies_new.svg?react"
import LabelPopular from "@/assets/svg/tapToAsk/trend_most_pop_new.svg?react"
import LabelTrending from "@/assets/svg/tapToAsk/trend_trend_new.svg?react"

interface TapToAskProps {
  onSelect: (question: string) => void
}

const TABS: { key: TagCategory; label: string }[] = [
  { key: "love", label: tapToAskCopy.love },
  { key: "career", label: tapToAskCopy.career },
  { key: "health", label: tapToAskCopy.health },
]

const LABEL_STYLES: Record<QuestionLabel, string> = {
  ladies: "text-peach bg-peach/20",
  popular: "text-gold bg-gold/20",
  trending: "text-green bg-green/20",
}

function QuestionLabelBadge({ label }: { label: QuestionLabel }) {
  return (
    <span
      className={cn(
        "flex h-[26px] items-center justify-center gap-1 text-nowrap rounded-[13px] px-2 font-body text-[10px] font-medium [&_svg]:h-3.5",
        LABEL_STYLES[label],
      )}
    >
      {label === "ladies" && <LabelLadies />}
      {label === "popular" && <LabelPopular />}
      {label === "trending" && <LabelTrending />}
      {tapToAskLabels[label]}
      {label === "popular" && <LabelPopular className="-scale-x-100" />}
    </span>
  )
}

export function TapToAsk({ onSelect }: TapToAskProps) {
  const [activeTab, setActiveTab] = useState<TagCategory>("love")

  return (
    <section className="relative w-full overflow-hidden bg-dark-base pt-10">
      <h2 className="px-4 font-display text-2xl font-medium text-purple-light">
        {tapToAskCopy.title}
      </h2>

      <div className="no-scrollbar mt-4 flex gap-4 overflow-x-auto px-4">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "flex h-12 w-[180px] flex-none items-center justify-center text-nowrap rounded-3xl font-body text-base font-semibold transition-colors",
                isActive
                  ? "bg-purple-light text-dark-base"
                  : "bg-purple-light/10 text-purple-light",
              )}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      <div className="relative z-[3] mt-4 flex flex-col gap-2">
        {tapToAskQuestions[activeTab].map((question, index) => {
          const label = tapToAskLabelMapping[activeTab][index]
          return (
            <button
              type="button"
              key={question}
              onClick={() => onSelect(question)}
              className="relative mx-auto flex w-[calc(100%-32px)] items-center gap-2 text-nowrap rounded-xl bg-purple-light/10 p-4 text-left font-body text-base text-purple-light"
            >
              <span className="flex items-center justify-between gap-1.5">
                <span>{question}</span>
                {label && <QuestionLabelBadge label={label} />}
              </span>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-purple-light [&_svg]:size-6">
                <Chevron />
              </span>
            </button>
          )
        })}
      </div>
    </section>
  )
}
